import { getPathTo as generatedPathTo } from './generated/get-path-to'

export const getPathTo = {
    ...generatedPathTo,
    authorizationsWizard: ({ contentId, additionalParams }: { contentId: number; additionalParams?: string }) => {
        const wizardUrl = '/core/authorization/add/'
        const params = new URLSearchParams()

        params.append('ct_id', contentId.toString())

        if (additionalParams) {
            // additionalParams is already URL-encoded from the API
            return `${wizardUrl}?${params.toString()}&${additionalParams}`
        }

        return `${wizardUrl}?${params.toString()}`
    },
    datastreamsWizard: ({
        authorizationId,
        contentId,
        additionalParams,
    }: {
        authorizationId?: number
        contentId?: number
        additionalParams?: string
    }) => {
        const wizardUrl = authorizationId ? '/core/apidatastream/preset/' : '/core/apidatastream/add/'
        const params = new URLSearchParams()

        if (authorizationId) {
            params.append('auth', authorizationId.toString())
        }

        if (contentId) {
            params.append('ct_id', contentId.toString())
        }

        const stringifiedParams = params.toString()

        // Append any additional params passed through from the redirect
        if (additionalParams) {
            return stringifiedParams
                ? `${wizardUrl}?${stringifiedParams}&${additionalParams}`
                : `${wizardUrl}?${additionalParams}`
        }

        return stringifiedParams ? `${wizardUrl}?${stringifiedParams}` : wizardUrl
    },
    destinationWizard: ({
        authorizationId,
        contentId,
        additionalParams,
    }: {
        authorizationId?: number
        contentId?: number
        additionalParams?: string
    }) => {
        const wizardUrl = '/target/basetarget/add/'
        const params = new URLSearchParams()

        if (authorizationId) {
            params.append('auth', authorizationId.toString())
        }

        if (contentId) {
            params.append('ct_id', contentId.toString())
        }

        const stringifiedParams = params.toString()

        // Append any additional params passed through from the redirect
        if (additionalParams) {
            return stringifiedParams
                ? `${wizardUrl}?${stringifiedParams}&${additionalParams}`
                : `${wizardUrl}?${additionalParams}`
        }

        return stringifiedParams ? `${wizardUrl}?${stringifiedParams}` : wizardUrl
    },
    enrichExport: (
        params: Partial<{
            polymorphic_ctype: string
            ordering: string
            workspace: string
            // oxlint-disable-next-line id-length
            q: string
        }> &
            ({ exclude_entity_ids: string } | { entity_ids: string }),
    ) => {
        const modifiedParams = new URLSearchParams(params).toString()

        return `/api/web-app/enrichments/actions/export/?${modifiedParams}`
    },
    /**
     * Atrium paths
     */
    authorizationDetails: ({ authorizationId, stackUuid }: { authorizationId: number; stackUuid: string }) =>
        `/workspaces/${stackUuid}/authorizations/${authorizationId}/`,
    authorizationsList: ({ stackUuid }: { stackUuid: string }) => `/workspaces/${stackUuid}/authorizations/`,
    authorizationsCreate: ({ stackUuid }: { stackUuid: string }) => `/workspaces/${stackUuid}/authorizations/create/`,
    cloneWorkspace: ({ workspaceUuid }: { workspaceUuid: string }) => `/workspaces/${workspaceUuid}/clone/`,
    dataDictionary: ({ stackUuid }: { stackUuid: string }) => `/workspaces/${stackUuid}/data-dictionary/`,
    destinations: ({ stackUuid }: { stackUuid: string }) => `/workspaces/${stackUuid}/destinations/`,
    enrichmentsList: ({ stackUuid }: { stackUuid: string }) => `/workspaces/${stackUuid}/enrichments/`,
    enrichmentsAdd: ({ stackUuid, datastreamId }: { stackUuid: string; datastreamId?: number }) => {
        const baseUrl = `/workspaces/${stackUuid}/enrichments/`
        const params = new URLSearchParams()

        params.append('create', 'new')

        if (datastreamId) {
            params.append('datastreamId', datastreamId.toString())
        }

        const stringifiedParams = params.toString()

        return `${baseUrl}?${stringifiedParams}`
    },
    enrichmentDetails: ({
        enrichmentId,
        stackUuid,
        datastreamId,
        extractId,
    }: {
        enrichmentId: number
        stackUuid: string
        datastreamId?: number
        extractId?: number
    }) => {
        const baseUrl = `/workspaces/${stackUuid}/enrichments/details/${enrichmentId}/`
        const params = new URLSearchParams()

        if (datastreamId) {
            params.append('datastreamId', datastreamId.toString())
        }

        if (extractId) {
            params.append('extractId', extractId.toString())
        }

        const stringifiedParams = params.toString()

        return stringifiedParams ? `${baseUrl}?${stringifiedParams}` : baseUrl
    },
    manageDatastream: ({ stackUuid, datastreamId }: { stackUuid: string; datastreamId: number }) =>
        `/workspaces/${stackUuid}/enrichments/manage/datastream/${datastreamId}/`,
    manageDatastreamEnrichmentDetails: ({
        stackUuid,
        datastreamId,
        enrichmentId,
    }: {
        stackUuid: string
        datastreamId: number
        enrichmentId: number
    }) => `/workspaces/${stackUuid}/enrichments/manage/datastream/${datastreamId}/enrichment/${enrichmentId}/edit/`,
    help: ({ stackUuid }: { stackUuid: string }) => `/workspaces/${stackUuid}/help/`,
    destinationCredentials: ({
        stackUuid,
        destinationUuid,
        biToolName,
    }: {
        stackUuid: string
        destinationUuid: string
        biToolName: string
    }) => `/workspaces/${stackUuid}/destinations/${destinationUuid}/credentials/?biToolName=${biToolName}`,
    systemManagedAuthorizationsList: ({ stackUuid }: { stackUuid: string }) =>
        `/workspaces/${stackUuid}/authorizations/system-managed-list/`,
    datastreamListing: ({ stackUuid }: { stackUuid: string }) => `/workspaces/${stackUuid}/datastreams/`,
    overview: ({ stackUuid }: { stackUuid: string }) => `/workspaces/${stackUuid}/overview/`,
    administrationWarehouse: ({ stackUuid }: { stackUuid: string }) =>
        `/workspaces/${stackUuid}/administration/warehouse/`,
    dataConversationsGetExportToGoogleSheetsUrl: (messageId: string) =>
        `/api/internal/ui/v1/data-conversations/messages/${messageId}/export`,
    extractionLogsList: ({ stackUuid }: { stackUuid: string }) => `/workspaces/${stackUuid}/activity/all-issues/`,
    allDataExtractsList: ({ stackUuid }: { stackUuid: string }) =>
        `/workspaces/${stackUuid}/activity/all-data-extracts/`,
}
